/**
 * @author    Nikko Renolds
 * @package   Trap Tycoon
 * @description UI for displaying Street Promoter (Advertiser & Booster) benefits.
 */

import React from "react";
import {
  TrendingUp,
  CheckCircle2,
  Circle,
  Zap,
  Server,
  X,
  ExternalLink,
} from "lucide-react";

export default function StreetPromoterUI({ stats, onClose }) {
  const isAdvertiser = !!stats.is_advertiser;
  const isBooster = !!stats.is_booster;

  const perks = [
    {
      id: "advertiser",
      title: "Street Advertiser",
      description: "Add 'trap-tycoon.net' to your Discord status/name.",
      active: isAdvertiser,
      benefits: [
        "+$5,000 Cash per Daily Claim",
        "+5 Extra Turns per Daily Claim",
        "10% Chance for random Dog items",
      ],
      icon: (
        <Server
          className={isAdvertiser ? "text-blue-400" : "text-zinc-600"}
          size={24}
        />
      ),
    },
    {
      id: "booster",
      title: "Nitro Booster",
      description: "Boost the official Trap Tycoon Discord server.",
      active: isBooster,
      benefits: [
        "10% Chance for random Dog items",
        "Exclusive 'Contributor' profile badge",
        "Priority support in the streets",
      ],
      icon: (
        <Zap
          className={isBooster ? "text-pink-500" : "text-zinc-600"}
          size={24}
        />
      ),
    },
  ];

  return (
    <div className="fixed inset-0 z-[7000] flex items-center justify-center p-4 bg-black/90 backdrop-blur-sm animate-in fade-in duration-300 font-mono">
      <div className="w-full max-w-lg bg-zinc-950 border-2 border-zinc-800 shadow-[20px_20px_0px_0px_rgba(0,0,0,1)] flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 p-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <TrendingUp size={24} className="text-black" />
            <div>
              <h2 className="text-xl font-black text-black uppercase italic leading-none">
                Promoter Protocol
              </h2>
              <p className="text-[10px] text-blue-900 font-bold uppercase tracking-tighter">
                Expand the influence. Reap the rewards.
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-blue-900 hover:text-black transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6 overflow-y-auto max-h-[80vh]">
          {/* Summary Status */}
          <div className="grid grid-cols-2 gap-4">
            <div
              className={`p-3 border ${isAdvertiser ? "border-blue-500 bg-blue-500/10" : "border-zinc-800 bg-zinc-900/50"}`}
            >
              <p className="text-[9px] text-zinc-500 uppercase font-black">
                Advertiser
              </p>
              <p
                className={`text-xs font-black uppercase ${isAdvertiser ? "text-blue-400" : "text-zinc-700"}`}
              >
                {isAdvertiser ? "ACTIVE" : "INACTIVE"}
              </p>
            </div>
            <div
              className={`p-3 border ${isBooster ? "border-pink-500 bg-pink-500/10" : "border-zinc-800 bg-zinc-900/50"}`}
            >
              <p className="text-[9px] text-zinc-500 uppercase font-black">
                Booster
              </p>
              <p
                className={`text-xs font-black uppercase ${isBooster ? "text-pink-400" : "text-zinc-700"}`}
              >
                {isBooster ? "ACTIVE" : "INACTIVE"}
              </p>
            </div>
          </div>

          {/* Detailed Perks */}
          {perks.map((perk) => (
            <div
              key={perk.id}
              className={`border-2 ${perk.active ? "border-zinc-700 bg-zinc-900/20" : "border-dashed border-zinc-800"}`}
            >
              <div className="p-4 flex items-start gap-4">
                <div className="p-2 bg-black border border-zinc-800">
                  {perk.icon}
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-sm font-black uppercase ${perk.active ? "text-white" : "text-zinc-500"}`}
                  >
                    {perk.title}
                  </h3>
                  <p className="text-[10px] text-zinc-600 uppercase mt-1 leading-tight">
                    {perk.description}
                  </p>
                </div>
              </div>

              <div className="bg-black/50 p-4 border-t border-zinc-800">
                <p className="text-[10px] text-zinc-500 font-bold uppercase mb-3 tracking-widest">
                  Protocol Benefits:
                </p>
                <div className="space-y-2">
                  {perk.benefits.map((benefit, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      {perk.active ? (
                        <CheckCircle2 size={12} className="text-green-500" />
                      ) : (
                        <Circle size={12} className="text-zinc-800" />
                      )}
                      <span
                        className={`text-[10px] font-bold uppercase ${perk.active ? "text-zinc-300" : "text-zinc-600"}`}
                      >
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}

          {/* Social Links */}
          <div className="pt-4 border-t border-zinc-900 flex flex-col gap-3">
            <button
              onClick={() =>
                window.open("https://discord.gg/trap-tycoon", "_blank")
              }
              className="w-full bg-indigo-600 hover:bg-indigo-500 text-white font-black py-3 text-xs uppercase italic flex items-center justify-center gap-2 transition-all"
            >
              Join the Discord <ExternalLink size={14} />
            </button>
            <p className="text-[8px] text-zinc-700 text-center uppercase tracking-widest">
              Rewards are processed automatically during daily claims.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
